import { Button, Form, Input, InputNumber, Modal, Select, message } from 'antd'
import React, { useEffect, useState } from 'react'
import { KpiV2Request } from '../../requests/KpiV2Request'

const LABEL_COL = { flex: '0 0 100px' }
const WRAPPER_COL = { flex: 1 }
const { Option } = Select

const getAspectParentId = aspect =>
  aspect?.parent_id || aspect?.parentId || null

const sumAspectWeight = list =>
  list.reduce((total, item) => total + (Number(item.weight) || 0), 0)

const getAspectDescendantIds = (aspectId, list) => {
  const directChildren = list.filter(asp => getAspectParentId(asp) === aspectId)
  return directChildren.reduce(
    (all, child) => [
      ...all,
      child.id,
      ...getAspectDescendantIds(child.id, list),
    ],
    []
  )
}

/**
 * Modal "Thêm trọng số" (Thêm Aspect) theo màn hình mockup.
 * Props:
 *  - visible: boolean
 *  - onClose: () => void
 *  - onSuccess: () => void  (gọi lại để refresh aspect list)
 *  - editData: object | null  (nếu có thì là chế độ cập nhật)
 */
const KpiV2AddWeightModal = ({
  visible,
  onClose,
  onSuccess,
  editData = null,
  aspects = [],
  defaultParentId = null,
  forceRoot = false,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const formatAspectSelectValue = aspectId => {
    if (!aspectId) return undefined
    const aspect = aspects.find(asp => asp.id === aspectId)
    return {
      value: aspectId,
      label: aspect?.context || aspectId,
    }
  }

  const getFormParentId = () => {
    const parent = form.getFieldValue('parent_id')
    return parent?.value || parent || null
  }

  const isEdit = Boolean(editData)

  useEffect(() => {
    if (!visible) {
      form.resetFields()
      return
    }
    if (isEdit && editData) {
      form.setFieldsValue({
        context: editData.context,
        weight: editData.weight,
        parent_id: formatAspectSelectValue(getAspectParentId(editData)),
      })
      return
    }
    if (defaultParentId) {
      form.setFieldsValue({
        parent_id: formatAspectSelectValue(defaultParentId),
      })
      return
    }
    if (forceRoot) {
      form.setFieldsValue({ parent_id: undefined })
      return
    }
    form.resetFields()
  }, [visible, editData, isEdit, form, defaultParentId, aspects])

  const invalidParentIds = editData?.id
    ? [editData.id, ...getAspectDescendantIds(editData.id, aspects)]
    : []
  const parentOptions = aspects.filter(
    asp => !invalidParentIds.includes(asp.id)
  )

  const validateWeight = (_, value) => {
    if (value == null) return Promise.resolve()

    const parentId = getFormParentId()
    const childTotal = editData?.id
      ? sumAspectWeight(
          aspects.filter(asp => getAspectParentId(asp) === editData.id)
        )
      : 0
    if (!parentId && childTotal > value) {
      return Promise.reject(
        new Error('Trọng số cha không được nhỏ hơn tổng trọng số các mục con')
      )
    }

    if (!parentId) return Promise.resolve()

    const parent = aspects.find(asp => asp.id === parentId)
    if (!parent?.weight) return Promise.resolve()

    const siblingTotal = sumAspectWeight(
      aspects.filter(
        asp => getAspectParentId(asp) === parentId && asp.id !== editData?.id
      )
    )
    if (siblingTotal + value > parent.weight) {
      return Promise.reject(
        new Error('Tổng trọng số mục con không được vượt quá trọng số mục cha')
      )
    }
    return Promise.resolve()
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const payload = {
        context: values.context,
        weight: values.weight,
        parent_id: values.parent_id?.value || null,
      }

      if (isEdit) {
        await KpiV2Request.updateAspect(editData.id, payload)
        message.success('Cập nhật trọng số thành công!')
      } else {
        await KpiV2Request.createAspect(payload)
        message.success('Thêm trọng số thành công!')
      }

      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      if (error?.errorFields) return
      const responseData = error?.response?.data
      const msg =
        error?.vi ||
        error?.en ||
        responseData?.message ||
        responseData?.errorMessage?.messages?.vi ||
        responseData?.messages?.vi ||
        responseData?.errorMessage?.messages?.en ||
        responseData?.messages?.en ||
        error?.message ||
        'Đã xảy ra lỗi, vui lòng thử lại.'
      message.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={isEdit ? 'Cập nhật trọng số' : 'Thêm trọng số'}
      visible={visible}
      onCancel={onClose}
      width={480}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Hủy bỏ</Button>
          <Button type={'primary'} loading={loading} onClick={handleSubmit}>
            {isEdit ? 'Lưu' : '+ Tạo'}
          </Button>
        </div>
      }>
      <Form
        form={form}
        layout={'horizontal'}
        labelCol={LABEL_COL}
        wrapperCol={WRAPPER_COL}
        labelAlign={'left'}
        colon={false}>
        {/* * Khía cạnh */}
        <Form.Item
          name={'context'}
          label={'Khía cạnh'}
          rules={[{ required: true, message: 'Vui lòng nhập khía cạnh' }]}
          style={{ marginBottom: 16 }}>
          <Input placeholder={'Nhập khía cạnh'} />
        </Form.Item>

        {/* * Trọng số */}
        <Form.Item
          name={'weight'}
          label={' Trọng số'}
          rules={[
            { required: true, message: 'Vui lòng nhập trọng số' },
            {
              type: 'number',
              min: 0,
              max: 100,
              message: 'Trọng số phải từ 0 đến 100',
            },
            { validator: validateWeight },
          ]}
          style={{ marginBottom: 0 }}>
          <InputNumber
            min={0}
            max={100}
            placeholder={'Nhập trọng số'}
            addonAfter={'%'}
            style={{ width: '100%' }}
          />
        </Form.Item>

        {!forceRoot && (
          <Form.Item
            name={'parent_id'}
            label={'Mục cha'}
            style={{ marginBottom: 16, display: 'none' }}>
            <Select
              placeholder={'Chọn mục cha'}
              allowClear
              labelInValue
              optionLabelProp="children">
              {parentOptions.map(asp => (
                <Option key={asp.id} value={asp.id}>
                  {asp.context}
                  {asp.weight != null ? ` (${asp.weight}%)` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

export default KpiV2AddWeightModal
